/**
 * „Do przejrzenia" as an inbox: which submission is open, where J and K move it, and where the
 * selection lands once one is marked reviewed.
 *
 * ── The selection is RESOLVED against the list, never trusted ────────────────────────────────
 * A submission marked reviewed leaves the queue on the refetch, and the list can change under the
 * trainer for other reasons too (a client reviewed it on another screen). So the id the trainer
 * picked is only a wish: `openedId` answers with it while the list still holds it and with the first
 * row otherwise, and the inbox never describes a row the server no longer returns.
 *
 * ── The keys belong to the inbox only while nothing else is typing ──────────────────────────
 * J and K step down and up the list (the mail-client pair the hint names), E marks the open one
 * reviewed and moves on. None of them fires while focus is in a text field, while a menu or a
 * dialog holds it, or with a modifier held — Ctrl+K and ⌘E are the browser's and the system's.
 * A held E does not repeat: one press is one submission marked.
 *
 * No React, no DOM, no query hook, no i18next.
 */

/** What a key press asks the inbox to do. */
export type InboxCommand = 'next' | 'previous' | 'review';

/** The parts of a key press this decision reads. The caller works out `captured` from the DOM. */
export interface InboxKeyPress {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly repeat: boolean;
  /** Focus is in a text field, a menu or a dialog — somewhere a letter means a letter. */
  readonly captured: boolean;
}

export function inboxCommand(press: InboxKeyPress): InboxCommand | null {
  if (press.captured || press.ctrlKey || press.metaKey || press.altKey) return null;
  switch (press.key.toLowerCase()) {
    case 'j':
      return 'next';
    case 'k':
      return 'previous';
    case 'e':
      return press.repeat ? null : 'review';
    default:
      return null;
  }
}

interface Identified {
  readonly assignmentId: number;
}

/** The submission the pane shows: the picked one while the list holds it, the first otherwise. */
export function openedId(rows: readonly Identified[], picked: number | null): number | null {
  if (picked !== null && rows.some((row) => row.assignmentId === picked)) return picked;
  return rows[0]?.assignmentId ?? null;
}

/**
 * The id the inbox should HOLD as picked: whatever the pane is showing, once it shows anything.
 *
 * A fallback to the first row must not stay a fallback. The queue refetches on focus, and a
 * submission that arrived meanwhile lands at the TOP — if the pick were still `null`, the pane would
 * swap to it under the trainer and E would mark a submission nobody had read. Pinning the resolved
 * id the moment a row is shown means only the trainer moves the selection. An empty list keeps
 * whatever was held, so nothing loops.
 */
export function pinnedId(rows: readonly Identified[], picked: number | null): number | null {
  return rows.length === 0 ? picked : openedId(rows, picked);
}

/** One step down (`1`) or up (`-1`) the list, stopping at either end. */
export function steppedId(
  rows: readonly Identified[],
  current: number | null,
  step: 1 | -1,
): number | null {
  const index = rows.findIndex((row) => row.assignmentId === current);
  if (index < 0) return rows[0]?.assignmentId ?? null;
  return rows[index + step]?.assignmentId ?? null;
}

/**
 * Where the selection goes once `reviewed` leaves the list: the row after it, or the one before it
 * when it was the last — the inbox keeps going down the queue rather than jumping back to the top.
 */
export function successorId(rows: readonly Identified[], reviewed: number): number | null {
  const index = rows.findIndex((row) => row.assignmentId === reviewed);
  if (index < 0) return null;
  return rows[index + 1]?.assignmentId ?? rows[index - 1]?.assignmentId ?? null;
}
